import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdLock, MdLockOpen } from 'react-icons/md';
import '../master/MasterData.css';

interface FiscalYear {
  yearId: number;
  yearName: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED';
  isCurrent: boolean;
  closedBy: string;
  closedDate: string;
}

interface FiscalPeriod {
  periodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED';
  fiscalYearId: number;
}

const FiscalYearSetup: React.FC = () => {
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([
    {
      yearId: 1,
      yearName: 'FY 2023',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      status: 'CLOSED',
      isCurrent: false,
      closedBy: 'Finance Manager',
      closedDate: '2024-01-15'
    },
    {
      yearId: 2,
      yearName: 'FY 2024',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      status: 'CLOSED',
      isCurrent: false,
      closedBy: 'Finance Manager',
      closedDate: '2025-01-10'
    },
    {
      yearId: 3,
      yearName: 'FY 2025',
      startDate: '2025-01-01',
      endDate: '2025-12-31',
      status: 'OPEN',
      isCurrent: true,
      closedBy: '',
      closedDate: ''
    }
  ]);

  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([
    { periodId: 1, periodName: 'January 2025', startDate: '2025-01-01', endDate: '2025-01-31', status: 'CLOSED', fiscalYearId: 3 },
    { periodId: 2, periodName: 'February 2025', startDate: '2025-02-01', endDate: '2025-02-28', status: 'CLOSED', fiscalYearId: 3 },
    { periodId: 3, periodName: 'March 2025', startDate: '2025-03-01', endDate: '2025-03-31', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 4, periodName: 'April 2025', startDate: '2025-04-01', endDate: '2025-04-30', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 5, periodName: 'May 2025', startDate: '2025-05-01', endDate: '2025-05-31', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 6, periodName: 'June 2025', startDate: '2025-06-01', endDate: '2025-06-30', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 7, periodName: 'July 2025', startDate: '2025-07-01', endDate: '2025-07-31', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 8, periodName: 'August 2025', startDate: '2025-08-01', endDate: '2025-08-31', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 9, periodName: 'September 2025', startDate: '2025-09-01', endDate: '2025-09-30', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 10, periodName: 'October 2025', startDate: '2025-10-01', endDate: '2025-10-31', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 11, periodName: 'November 2025', startDate: '2025-11-01', endDate: '2025-11-30', status: 'OPEN', fiscalYearId: 3 },
    { periodId: 12, periodName: 'December 2025', startDate: '2025-12-31', endDate: '2025-12-31', status: 'OPEN', fiscalYearId: 3 }
  ]);

  const [showYearModal, setShowYearModal] = useState(false);
  const [editingYear, setEditingYear] = useState<FiscalYear | null>(null);
  const [selectedYearId, setSelectedYearId] = useState(3);

  const handleAddYear = () => {
    setEditingYear(null);
    setShowYearModal(true);
  };

  const handleEditYear = (year: FiscalYear) => {
    if (year.status === 'CLOSED') {
      alert('Cannot edit a closed fiscal year');
      return;
    }
    setEditingYear(year);
    setShowYearModal(true);
  };

  const handleCloseYear = (yearId: number) => {
    if (window.confirm('Are you sure you want to close this fiscal year? This action cannot be undone.')) {
      setFiscalYears(fiscalYears.map(y =>
        y.yearId === yearId
          ? { ...y, status: 'CLOSED', closedBy: 'Current User', closedDate: new Date().toISOString().split('T')[0] }
          : y
      ));
      alert('Fiscal year closed successfully');
    }
  };

  const handleClosePeriod = (periodId: number) => {
    if (window.confirm('Are you sure you want to close this period? No more transactions can be posted to this period.')) {
      setFiscalPeriods(fiscalPeriods.map(p =>
        p.periodId === periodId ? { ...p, status: 'CLOSED' } : p
      ));
      alert('Period closed successfully');
    }
  };

  const handleReopenPeriod = (periodId: number) => {
    if (window.confirm('Are you sure you want to reopen this period?')) {
      setFiscalPeriods(fiscalPeriods.map(p =>
        p.periodId === periodId ? { ...p, status: 'OPEN' } : p
      ));
      alert('Period reopened successfully');
    }
  };

  const currentYear = fiscalYears.find(y => y.isCurrent);
  const selectedYear = fiscalYears.find(y => y.yearId === selectedYearId);
  const yearPeriods = fiscalPeriods.filter(p => p.fiscalYearId === selectedYearId);

  const summary = {
    totalYears: fiscalYears.length,
    openYears: fiscalYears.filter(y => y.status === 'OPEN').length,
    closedYears: fiscalYears.filter(y => y.status === 'CLOSED').length,
    openPeriods: yearPeriods.filter(p => p.status === 'OPEN').length
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Fiscal Year Setup</h2>
            <p className="subtitle">Manage fiscal years and accounting periods</p>
          </div>
          <button className="btn btn-primary" onClick={handleAddYear}>
            <MdAdd /> Create New Fiscal Year
          </button>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📅</div>
            <div className="summary-content">
              <p className="summary-label">Total Fiscal Years</p>
              <p className="summary-value">{summary.totalYears}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🔓</div>
            <div className="summary-content">
              <p className="summary-label">Open Years</p>
              <p className="summary-value" style={{ color: '#28a745' }}>{summary.openYears}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🔒</div>
            <div className="summary-content">
              <p className="summary-label">Closed Years</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>{summary.closedYears}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📆</div>
            <div className="summary-content">
              <p className="summary-label">Open Periods (Current Year)</p>
              <p className="summary-value" style={{ color: '#17a2b8' }}>{summary.openPeriods}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          {/* Current Fiscal Year Info */}
          {currentYear && (
            <div style={{
              padding: '20px',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              marginBottom: '30px',
              border: '2px solid #1a237e'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#1a237e' }}>Current Fiscal Year</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
                <div>
                  <strong>Year:</strong> {currentYear.yearName}
                </div>
                <div>
                  <strong>Start Date:</strong> {new Date(currentYear.startDate).toLocaleDateString('en-US')}
                </div>
                <div>
                  <strong>End Date:</strong> {new Date(currentYear.endDate).toLocaleDateString('en-US')}
                </div>
                <div>
                  <strong>Status:</strong> <span className="badge badge-success">{currentYear.status}</span>
                </div>
              </div>
            </div>
          )}

          {/* Fiscal Years Table */}
          <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>Fiscal Years</h3>
          <div className="data-table-container" style={{ marginBottom: '30px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Year Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Current</th>
                  <th>Closed By</th>
                  <th>Closed Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fiscalYears.map(year => (
                  <tr key={year.yearId} style={{
                    backgroundColor: year.isCurrent ? '#f0f7ff' : 'transparent'
                  }}>
                    <td className="name">{year.yearName}</td>
                    <td>{new Date(year.startDate).toLocaleDateString('en-US')}</td>
                    <td>{new Date(year.endDate).toLocaleDateString('en-US')}</td>
                    <td>
                      <span className={`badge ${year.status === 'OPEN' ? 'badge-success' : 'badge-secondary'}`}>
                        {year.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {year.isCurrent && <span className="badge badge-primary">CURRENT</span>}
                    </td>
                    <td>{year.closedBy || '-'}</td>
                    <td>{year.closedDate ? new Date(year.closedDate).toLocaleDateString('en-US') : '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {year.status === 'OPEN' && (
                          <>
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => handleEditYear(year)}
                              title="Edit"
                            >
                              <MdEdit />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => handleCloseYear(year.yearId)}
                              title="Close Year"
                              style={{ color: '#dc3545' }}
                            >
                              <MdLock />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Fiscal Periods */}
          <h3 style={{ marginBottom: '15px', color: '#1a237e' }}>
            Accounting Periods
            {selectedYear && ` - ${selectedYear.yearName}`}
          </h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Select Fiscal Year:</label>
            <select
              value={selectedYearId}
              onChange={(e) => setSelectedYearId(Number(e.target.value))}
              style={{
                padding: '8px 15px',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                fontSize: '14px'
              }}
            >
              {fiscalYears.map(year => (
                <option key={year.yearId} value={year.yearId}>
                  {year.yearName} ({year.status})
                </option>
              ))}
            </select>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Period Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {yearPeriods.map(period => (
                  <tr key={period.periodId}>
                    <td className="name">{period.periodName}</td>
                    <td>{new Date(period.startDate).toLocaleDateString('en-US')}</td>
                    <td>{new Date(period.endDate).toLocaleDateString('en-US')}</td>
                    <td>
                      <span className={`badge ${period.status === 'OPEN' ? 'badge-success' : 'badge-secondary'}`}>
                        {period.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {period.status === 'OPEN' ? (
                          <button
                            className="btn-icon"
                            onClick={() => handleClosePeriod(period.periodId)}
                            title="Close Period"
                            style={{ color: '#dc3545' }}
                          >
                            <MdLock />
                          </button>
                        ) : (
                          <button
                            className="btn-icon"
                            onClick={() => handleReopenPeriod(period.periodId)}
                            title="Reopen Period"
                            style={{ color: '#28a745' }}
                          >
                            <MdLockOpen />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showYearModal && (
        <div className="modal-overlay" onClick={() => setShowYearModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingYear ? 'Edit Fiscal Year' : 'Create New Fiscal Year'}</h3>
              <button className="close-btn" onClick={() => setShowYearModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Year Name *</label>
                <input
                  type="text"
                  placeholder="e.g., FY 2026"
                  defaultValue={editingYear?.yearName}
                />
              </div>
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  defaultValue={editingYear?.startDate}
                />
              </div>
              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  defaultValue={editingYear?.endDate}
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    defaultChecked={editingYear?.isCurrent}
                  />
                  Set as Current Fiscal Year
                </label>
              </div>
              <div style={{
                padding: '15px',
                backgroundColor: '#fff3cd',
                borderRadius: '6px',
                border: '1px solid #ffc107',
                marginTop: '15px'
              }}>
                <strong>Note:</strong> Monthly periods will be automatically generated based on the start and end dates.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowYearModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary">
                {editingYear ? 'Update' : 'Create'} Fiscal Year
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default FiscalYearSetup;
